/**
 * MissionControl — Dashboard orienté valeur et action
 * 
 * Remplace la vue stats-only par un centre de commande qui montre :
 * 1. Ce que l'utilisateur peut FAIRE (actions orientées résultat)
 * 2. La VALEUR générée (KPIs concrets)
 * 3. Un parcours guidé par PERSONA (Dev / QA / PO)
 * 4. L'entrée vers le "Safe Change Flow"
 * 
 * Isolation : ce composant est 100% standalone.
 * Il peut être activé/désactivé via feature flag sans impact.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FaRocket, FaShieldAlt, FaFlask, FaProjectDiagram,
  FaChartLine, FaBullseye, FaCode, FaClipboardCheck,
  FaLightbulb, FaArrowRight, FaNetworkWired, FaLink,
  FaUserCog, FaUserShield, FaUserTie, FaPlay
} from 'react-icons/fa';
import apiClient from '../utils/apiClient';
import { handleAuthError } from '../utils/authErrorHandler';
import './MissionControl.css';

// ─── Persona definitions ───────────────────────────────────────
const PERSONAS = {
  dev: {
    id: 'dev',
    icon: FaCode,
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    actions: [
      { id: 'import-code', icon: FaCode, section: 'section-import-code' },
      { id: 'create-node', icon: FaProjectDiagram, section: 'section-quick-create' },
      { id: 'view-graph', icon: FaNetworkWired, section: 'section-graph' },
      { id: 'analyze-impact', icon: FaShieldAlt, section: 'section-analysis' },
    ]
  },
  qa: {
    id: 'qa',
    icon: FaFlask,
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    actions: [
      { id: 'generate-tests', icon: FaClipboardCheck, section: 'section-tests' },
      { id: 'check-coverage', icon: FaChartLine, section: 'section-analysis' },
      { id: 'quality-assistant', icon: FaFlask, section: 'section-vector-search' },
      { id: 'view-scenarios', icon: FaBullseye, section: 'section-tests' },
    ]
  },
  po: {
    id: 'po',
    icon: FaUserTie,
    gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    actions: [
      { id: 'specifications', icon: FaLightbulb, section: 'section-specifications' },
      { id: 'explore-graph', icon: FaNetworkWired, section: 'section-graph' },
      { id: 'check-coverage', icon: FaChartLine, section: 'section-analysis' },
      { id: 'export-docs', icon: FaClipboardCheck, section: 'section-export' },
    ]
  }
};

const MissionControl = ({ user, nodeStats, onNavigateToLab, onNavigateToSection, onSafeChangeFlow, motivationalMessage, onOpenDMBTGuide, licenseWidget }) => {
  const { t } = useTranslation();
  const [selectedPersona, setSelectedPersona] = useState(() => {
    return localStorage.getItem('mc_persona') || null;
  });
  const [valueKpis, setValueKpis] = useState({
    risksAnalyzed: 0,
    testsGenerated: 0,
    coveragePercent: 0,
    nodesDocumented: 0
  });
  const [recentActions, setRecentActions] = useState([]);
  const [loadingKpis, setLoadingKpis] = useState(true);

  // ─── Load KPIs ───────────────────────────────────────────────
  useEffect(() => {
    loadValueKpis();
  }, []);

  const loadValueKpis = useCallback(async () => {
    setLoadingKpis(true);
    try {
      const response = await apiClient.get('/api/user/stats');
      const data = response.data;
      setValueKpis({
        risksAnalyzed: data.meta?.impactAnalyses || 0,
        testsGenerated: data.meta?.testsCreated || 0,
        coveragePercent: data.nodes?.testCoverage || 0,
        nodesDocumented: data.nodes?.total || 0
      });
    } catch (error) {
      if (handleAuthError(error)) return;
      console.error('MissionControl: KPIs load error', error);
    } finally {
      setLoadingKpis(false);
    }
  }, []);

  // ─── Persona selection ───────────────────────────────────────
  const handlePersonaSelect = (personaId) => {
    setSelectedPersona(personaId);
    localStorage.setItem('mc_persona', personaId);
  };

  const handleActionClick = (action) => {
    if (onNavigateToSection) {
      onNavigateToSection(action.section, selectedPersona);
    }
  };

  const handleSafeChangeFlow = () => {
    // If parent passed a safe change flow handler, use it (opens modal)
    if (onSafeChangeFlow) {
      onSafeChangeFlow(selectedPersona);
    } else if (onNavigateToSection) {
      onNavigateToSection('section-analysis', selectedPersona);
    }
  };

  // ─── Render ──────────────────────────────────────────────────
  const persona = selectedPersona ? PERSONAS[selectedPersona] : null;

  return (
    <div className="mission-control">
      {/* ─── Value Header ─── */}
      <section className="mc-value-header">
        <div className="mc-value-tagline">
          {user?.firstName && (
            <p className="mc-greeting-line">Hi {user.firstName} 👋{motivationalMessage ? ` — ${motivationalMessage}` : ''}</p>
          )}
          <h2>{t('missionControl.tagline')}</h2>
          <p className="mc-value-subtitle">{t('missionControl.subtitle')}</p>
        </div>
        {licenseWidget && (
          <div className="mc-header-actions">
            {licenseWidget}
          </div>
        )}
      </section>

      {/* ─── Safe Change Flow (hero CTA) ─── */}
      <section className="mc-safe-change">
        <div className="mc-safe-change-card">
          <div className="mc-safe-change-content">
            <div className="mc-safe-change-icon">
              <FaShieldAlt />
            </div>
            <div className="mc-safe-change-text">
              <h3>{t('missionControl.safeChange.title')}</h3>
              <p>{t('missionControl.safeChange.desc')}</p>
            </div>
          </div>
          <div className="mc-safe-change-steps">
            <span className="mc-step">{t('missionControl.safeChange.step1')}</span>
            <FaArrowRight className="mc-step-arrow" />
            <span className="mc-step">{t('missionControl.safeChange.step2')}</span>
            <FaArrowRight className="mc-step-arrow" />
            <span className="mc-step">{t('missionControl.safeChange.step3')}</span>
          </div>
          <button className="mc-safe-change-btn" onClick={handleSafeChangeFlow}>
            <FaPlay /> {t('missionControl.safeChange.cta')}
          </button>
        </div>
      </section>

      {/* ─── Value KPIs ─── */}
      <section className="mc-kpis">
        <h3 className="mc-section-title">{t('missionControl.kpis.title')}</h3>
        <div className="mc-kpi-grid">
          <div className="mc-kpi-card mc-kpi-nodes">
            <FaNetworkWired className="mc-kpi-icon" />
            <span className="mc-kpi-value">{loadingKpis ? '...' : valueKpis.nodesDocumented}</span>
            <span className="mc-kpi-label">{t('missionControl.kpis.nodesDocumented')}</span>
          </div>
          <div className="mc-kpi-card mc-kpi-tests">
            <FaClipboardCheck className="mc-kpi-icon" />
            <span className="mc-kpi-value">{loadingKpis ? '...' : valueKpis.testsGenerated}</span>
            <span className="mc-kpi-label">{t('missionControl.kpis.testsGenerated')}</span>
          </div>
          <div className="mc-kpi-card mc-kpi-coverage">
            <FaChartLine className="mc-kpi-icon" />
            <span className="mc-kpi-value">{loadingKpis ? '...' : `${valueKpis.coveragePercent}%`}</span>
            <span className="mc-kpi-label">{t('missionControl.kpis.coveragePercent')}</span>
          </div>
          <div className="mc-kpi-card mc-kpi-risks">
            <FaShieldAlt className="mc-kpi-icon" />
            <span className="mc-kpi-value">{loadingKpis ? '...' : valueKpis.risksAnalyzed}</span>
            <span className="mc-kpi-label">{t('missionControl.kpis.risksAnalyzed')}</span>
          </div>
        </div>
      </section>

      {/* ─── Persona Selector ─── */}
      <section className="mc-persona">
        <h3 className="mc-section-title">{t('missionControl.persona.title')}</h3>
        <p className="mc-persona-subtitle">{t('missionControl.persona.subtitle')}</p>
        <div className="mc-persona-grid">
          {Object.entries(PERSONAS).map(([id, p]) => {
            const IconComponent = p.icon;
            return (
              <button
                key={id}
                className={`mc-persona-card ${selectedPersona === id ? 'mc-persona-active' : ''}`}
                style={selectedPersona === id ? { background: p.gradient } : {}}
                onClick={() => handlePersonaSelect(id)}
              >
                <IconComponent className="mc-persona-icon" />
                <span className="mc-persona-name">{t(`missionControl.persona.${id}.name`)}</span>
                <span className="mc-persona-desc">{t(`missionControl.persona.${id}.desc`)}</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* ─── Persona Actions (shown after selection) ─── */}
      {persona && (
        <section className="mc-actions">
          <h3 className="mc-section-title">
            {t('missionControl.actions.title', { persona: t(`missionControl.persona.${selectedPersona}.name`) })}
          </h3>
          <div className="mc-action-grid">
            {persona.actions.map((action) => {
              const ActionIcon = action.icon;
              return (
                <button
                  key={action.id}
                  className="mc-action-card"
                  onClick={() => handleActionClick(action)}
                >
                  <ActionIcon className="mc-action-icon" />
                  <div className="mc-action-text">
                    <h4>{t(`missionControl.actions.${action.id}.title`)}</h4>
                    <p>{t(`missionControl.actions.${action.id}.desc`)}</p>
                  </div>
                  <FaArrowRight className="mc-action-arrow" />
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* ─── Quick Lab Access ─── */}
      <section className="mc-lab-access">
        <button className="mc-lab-btn" onClick={onNavigateToLab}>
          <FaRocket className="mc-lab-icon" />
          <div className="mc-lab-text">
            <span className="mc-lab-title">{t('missionControl.lab.title')}</span>
            <span className="mc-lab-subtitle">{t('missionControl.lab.subtitle')}</span>
          </div>
          <FaArrowRight className="mc-lab-arrow" />
        </button>
      </section>
    </div>
  );
};

export default MissionControl;
